import { MenuItem } from "@chakra-ui/react";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { VThreeDotMenu } from "components/Menu/ThreeDotMenu";
import { PropsWithChildren } from "react";
import { ActionSummary } from "features/Resources/Shared/models";
import { fromActionPrefixId, isProcessed } from "features/Resources/Shared/helpers";
import { confirmRemoveArgs, confirmSendNowArgs } from "features/Resources/Shared/helpers/confirmationArgs";
import { useForceSendByPrefixId, useRemoveByPrefixId, useSkipByPrefixId } from "features/Resources/Shared/hooks";
import CaminoSendNow from "components/Icon/CaminoSendNow";
import CaminoSkip from "components/Icon/CaminoSkip";
import CaminoDelete from "components/Icon/CaminoDelete";
import MenuItemIcon from "components/Icon/MenuItemIcon";
import { useQueryClient } from "@tanstack/react-query";
import { meetingKeys } from "features/Resources/Meeting/hooks";

interface MiniResourceDotMenuProps extends PropsWithChildren {
  action: ActionSummary["action"];
}

export default function MiniResourceDotMenu({ action }: MiniResourceDotMenuProps) {
  const { resourceType } = fromActionPrefixId(action?.prefixId);
  const queryClient = useQueryClient();

  const invalidateMeetings = () => {
    if (resourceType === "meeting") {
      queryClient.invalidateQueries({ queryKey: meetingKeys.byNewHireJourney(action.newHireJourneyId) });
    }
  };

  const { mutate: handleForceSend } = useForceSendByPrefixId(action?.prefixId);
  const { mutate: handleSkip } = useSkipByPrefixId(action?.prefixId, invalidateMeetings);
  const { mutate: handleRemove } = useRemoveByPrefixId(action?.prefixId, invalidateMeetings);

  if (!action || isProcessed(action)) {
    return null;
  }
  const includeForceSend = (["email", "message"].includes(resourceType || "") && !action.deliveredAt) || (resourceType === "task" && !action.startedAt);

  return (
    <VThreeDotMenu menuFontSize="sm" size="2xs" inPortal>
      {includeForceSend && (
        <ButtonWithConfirm
          {...confirmSendNowArgs(resourceType)}
          intent="confirmation"
          handleConfirm={() => handleForceSend()}
        >
          <MenuItem icon={<MenuItemIcon icon={CaminoSendNow} size="sm" />}>{resourceType === "task" ? "Start Now" : "Send Now"}</MenuItem>
        </ButtonWithConfirm>
      )}
      <MenuItem icon={<MenuItemIcon icon={CaminoSkip} size="sm" />} onClick={() => handleSkip()}>
        {action.status === "skipped" ? "Unskip" : "Skip"}
      </MenuItem>
      <ButtonWithConfirm
        {...confirmRemoveArgs(resourceType)}
        intent="confirmation"
        handleConfirm={() => handleRemove()}
      >
        <MenuItem icon={<MenuItemIcon icon={CaminoDelete} size="sm" />}>Delete</MenuItem>
      </ButtonWithConfirm>
    </VThreeDotMenu>
  );
}
